import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { settingsApi, uploadApi } from '../../../api';
import { useUIStore } from '../../../store';

const WhatsAppProfilePage = ({ sess: _sess = { uid: '', role: 'user' } }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    business_name: '',
    description: '',
    address: '',
    email: '',
    website: '',
    category: '',
    profile_picture: '',
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const showSnackbar = useUIStore((state) => state.showSnackbar);

  const categories = [
    'Retail',
    'Restaurant',
    'Healthcare',
    'Education',
    'Finance',
    'Real Estate',
    'Technology',
    'Other',
  ];

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status.type !== 'success' || !status.message) return;
    showSnackbar({ message: status.message, severity: 'success' });
    setStatus({ type: '', message: '' });
  }, [showSnackbar, status]);

  const deriveFriendlyError = (error) => {
    const raw =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      '';
    const isPhoneIdIssue =
      /Unknown path components/i.test(raw) ||
      /phone number id/i.test(raw) ||
      /phone_numbers/i.test(raw);
    if (isPhoneIdIssue) {
      setConnectionError(true);
      return 'WhatsApp connection looks invalid. Please reconnect and ensure a valid phone number is selected.';
    }
    setConnectionError(false);
    return raw || 'Unable to load WhatsApp profile';
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await settingsApi.getWhatsAppProfile();
      const payload = response?.data || {};
      const profile = payload?.data?.profile || payload?.profile || payload?.data || {};
      const displayName = payload?.data?.display_name || payload?.display_name || {};

      if (Object.keys(profile).length > 0 || Object.keys(displayName).length > 0) {
        setFormData((prev) => {
          const resolvedCategory = categories.includes(profile.vertical)
            ? profile.vertical
            : profile.vertical
              ? 'Other'
              : prev.category;

          return {
            ...prev,
            business_name: displayName?.verified_name || prev.business_name,
            description: profile.description || profile.about || prev.description,
            address: profile.address || prev.address,
            email: profile.email || prev.email,
            website: (profile.websites && profile.websites[0]) || prev.website,
            category: resolvedCategory,
            profile_picture: profile.profile_picture_url || profile.profile_picture || prev.profile_picture,
          };
        });
        setImagePreview(profile.profile_picture_url || profile.profile_picture || '');
        setStatus({ type: '', message: '' });
        setConnectionError(false);
      }
    } catch (error) {
      const message = deriveFriendlyError(error);
      setStatus({ type: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      setUploadingImage(true);
      const response = await uploadApi.uploadImage(formDataUpload);
      const { url } = response?.data || {};
      if (url) {
        setImagePreview(url);
        setFormData((prev) => ({ ...prev, profile_picture: url }));
        setStatus({ type: 'success', message: 'Image uploaded successfully' });
      } else {
        throw new Error('Invalid upload response');
      }
    } catch (error) {
      const message = error?.response?.data?.message || 'Error uploading image';
      setStatus({ type: 'error', message });
    }
    finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const response = await settingsApi.updateWhatsAppProfile(formData);
      const message = response?.data?.message || 'WhatsApp profile updated successfully';
      setStatus({ type: 'success', message });
      await fetchProfile();
    } catch (error) {
      const message = deriveFriendlyError(error);
      setStatus({ type: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
        p: { xs: 2, md: 3 },
        pb: { xs: 10, md: 12 },
      }}
    >
      <Box maxWidth="1100px" mx="auto">
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <Button
            variant="text"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/settings')}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            Back
          </Button>
        </Stack>

        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            mb: 3,
            background: (theme) =>
              `linear-gradient(120deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            color: 'primary.contrastText',
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Smartphone size={26} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={800}>
                  WhatsApp Business Profile
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Configure your business profile for customer-facing WhatsApp details.
                </Typography>
              </Box>
              <Chip
                label={connectionError ? 'Action Required' : 'Connected'}
                color={connectionError ? 'warning' : 'success'}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
              />
            </Stack>
          </CardContent>
        </Card>

        <Stack spacing={1.5} mb={2}>
          <Alert icon={<Smartphone size={18} />} severity="info">
            Configure your WhatsApp Business profile to provide customers with important business information.
          </Alert>
          {status.message && (
            status.type === 'error' && <Alert severity="error">{status.message}</Alert>
          )}
          {connectionError && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <Button
                variant="outlined"
                onClick={() => navigate('/facebookDetails')}
                size="small"
              >
                Reconnect WhatsApp
              </Button>
              <Typography variant="body2" color="text.secondary">
                Reconnect your WhatsApp Business account and select the correct phone number, then try again.
              </Typography>
            </Stack>
          )}
        </Stack>

        <Card
          sx={{
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            background: '#ffffff',
          }}
        >
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Stack spacing={2.5}>
                    <TextField
                      label="Business Name"
                      required
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      fullWidth
                    />

                    <TextField
                      label="Business Description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      fullWidth
                      multiline
                      rows={3}
                      inputProps={{ maxLength: 256 }}
                      helperText="Max 256 characters"
                    />

                    <TextField
                      label="Business Address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      fullWidth
                    />

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Website"
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          fullWidth
                          placeholder="https://example.com"
                        />
                      </Grid>
                    </Grid>

                    <FormControl fullWidth>
                      <InputLabel id="category-label">Business Category</InputLabel>
                      <Select
                        labelId="category-label"
                        label="Business Category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                      >
                        <MenuItem value="">
                          <em>Select Category</em>
                        </MenuItem>
                        {categories.map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            {cat}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack spacing={2} alignItems="center">
                    <Typography variant="subtitle2" fontWeight={600} alignSelf="flex-start">
                      Profile Picture
                    </Typography>
                    <Avatar
                      src={imagePreview}
                      alt="Profile"
                      variant="rounded"
                      sx={{ width: 180, height: 180, borderRadius: 2 }}
                    />
                    <Button component="label" variant="outlined" fullWidth disabled={uploadingImage}>
                      Upload Image
                      <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                    </Button>
                    {uploadingImage && <Typography variant="caption">Uploading image...</Typography>}
                    <Typography variant="caption" color="text.secondary">
                      Recommended: 640x640px
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Stack direction="row" justifyContent="flex-end" spacing={1.5} mt={3}>
                <Button variant="outlined" onClick={() => navigate('/settings')}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={submitting}>
                  {submitting ? 'Updating...' : 'Update Profile'}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default WhatsAppProfilePage;
